import { ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { DueDiligencePageGetDueDiligenceRecordQuery, Role } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Textarea from '@/components/basicComponents/textarea';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { themeVar } from '@/styles/theme';
import { DueDiligenceRecordTierChecklistItem, DueDiligenceRecordTierSection } from '../dueDiligenceRecord/types';
import { ASSIGN_CHECKLIST_ITEM_MUTATION, ASSIGN_SECTION_MUTATION, LIST_USERS_QUERY } from '../queries';

interface ChangeAssigneeProps {
  isOpen: boolean;
  row: DueDiligenceRecordTierSection | DueDiligenceRecordTierChecklistItem | null;
  owner?: DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['owner'];
  dueDiligenceRecordRefetch: () => Promise<ApolloQueryResult<DueDiligencePageGetDueDiligenceRecordQuery>>;
  onClose: () => void;
}

const ChangeAssignee = ({ isOpen, row, owner, dueDiligenceRecordRefetch, onClose }: ChangeAssigneeProps) => {
  const { user } = useAuth();
  const { default: defaultErrorMessage } = useErrorMessages();

  const [assigneeSelectOptions, setAssigneeSelectOptions] = useState<ISelectValue[]>([]);
  const [assignedToId, setAssignedToId] = useState('');
  const [noteValue, setNoteValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [updateChecklistAssignee] = useMutation(ASSIGN_CHECKLIST_ITEM_MUTATION);
  const [updateSectionAssignee] = useMutation(ASSIGN_SECTION_MUTATION);

  useEffect(() => {
    setAssignedToId(row?.data?.assignedTo?.id || owner?.id || '');
  }, [row, owner]);

  const { loading: listUsersLoading } = useQuery(LIST_USERS_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      filters: {
        roles: [Role.InvestmentLead, Role.InvestmentMember],
        tenantId: user.tenant.id
      }
    },
    onCompleted: (data) => {
      const selectOptions = data.listUsers.map((user) => createSimpleSelectOption(user.id, `${user.firstName} ${user.lastName}`));
      setAssigneeSelectOptions(selectOptions);
    }
  });

  const changeAssignee = async () => {
    try {
      setErrorMessage(null);
      setLoading(true);

      if (!assignedToId) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      if (row.__typename.includes('Section')) {
        await updateSectionAssignee({
          variables: {
            data: {
              id: row.data.id,
              note: noteValue,
              assignedToId
            }
          }
        });
      }

      if (row.__typename.includes('Checklist')) {
        await updateChecklistAssignee({
          variables: {
            data: {
              id: row.data.id,
              note: noteValue,
              assignedToId
            }
          }
        });
      }

      await dueDiligenceRecordRefetch();
      onClose();
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Change Assignee"
      showCloseIcon={!loading}
      showCancelButton={!loading}
      isUseOutsideClick={!loading}
      showRequiredFields={!loading}
      confirmButton={{ name: 'Assign', onClick: changeAssignee, disabled: loading }}
    >
      {loading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <ChangeAssigneeWrapper>
            <Select
              label="Select An Advisor"
              value={assignedToId}
              setValue={setAssignedToId}
              options={assigneeSelectOptions}
              loading={listUsersLoading}
              size="md"
              isRequiredField
            />
            <Textarea label="Notes" value={noteValue} onChange={(e) => setNoteValue(e.target.value)} limitCharactersTo={250} size="md" />
          </ChangeAssigneeWrapper>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default ChangeAssignee;

const ChangeAssigneeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;
